import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Flex, theme } from 'antd';
import { Link } from 'react-router-dom';

export interface InfoBoxProps {
  intent?: 'primary' | 'secondary';
  title?: string;
  children: React.ReactNode;
  link?: {
    to: string;
    text: string;
  };
}

/**
 * This component is used to display an info box with a title, children, and a link.
 * Primarily built for the campaign types modal.
 * @param intent - The intent of the info box. Default is primary.
 * @param title - The title of the info box.
 * @param children - The children of the info box.
 * @param link - The link of the info box.
 * @returns The info box component.
 */
export const InfoBoxEcho = ({ intent = 'primary', title, children, link }: InfoBoxProps) => {
  const { token } = theme.useToken();
  const isPrimary = intent === 'primary';

  const description = (
    <Flex vertical gap={token.marginXS}>
      {children}
      {link && (
        <Link
          to={link.to}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${link.text} (opens in new tab)`}
          style={{
            color: isPrimary ? token.colorPrimary : token.colorText,
            fontWeight: token.fontWeightStrong,
            fontSize: token.fontSizeSM,
          }}
        >
          {link.text}
        </Link>
      )}
    </Flex>
  );

  return (
    <Alert
      type="info"
      showIcon
      icon={
        <InfoCircleOutlined
          style={{
            color: isPrimary ? token.colorInfoText : token.colorTextSecondary
          }}
        />
      }
      title={title}
      description={description}
      styles={{
        root: {
          background: isPrimary ? token.colorInfoBg : token.colorFillTertiary,
          borderColor: isPrimary ? token.colorInfoBorder : token.colorBorderSecondary,
          padding: token.paddingMD,
        },
      }}
    />
  );
};
